import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  FaArrowLeft, FaArrowRight, FaCode, FaGamepad, FaGift, FaLayerGroup, FaTv,
} from 'react-icons/fa'
import { resolveImageUrl } from '../../utilies/imageUrl'
import { getCategoryLabel } from '../../utilies/categoryLabels'

const GRADIENTS = [
  'from-violet-500 to-indigo-600',
  'from-fuchsia-500 to-purple-600',
  'from-sky-500 to-indigo-500',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
]

const DISCOVERY = [
  { labelKey: 'category.discovery.game', icon: FaGamepad, gradient: 'from-violet-500 to-indigo-600' },
  { labelKey: 'category.discovery.giftCard', icon: FaGift, gradient: 'from-rose-500 to-orange-500' },
  { labelKey: 'category.discovery.subscription', icon: FaTv, gradient: 'from-sky-500 to-cyan-500' },
  { labelKey: 'category.discovery.software', icon: FaCode, gradient: 'from-emerald-500 to-teal-600' },
]

const CategoryCard = ({ to, label, image, icon, gradient, badge, all, responsiveClass = '' }) => {
  const { t } = useTranslation()
  return (
  <Link
    to={to}
    className={`group flex h-[54px] w-[calc((100%_-_90px)/10)] shrink-0 snap-start items-center gap-2 rounded-xl border border-slate-200/70 bg-slate-50/40 px-2 transition-[color,background-color,border-color,transform,box-shadow] duration-200 hover:-translate-y-px hover:border-brand-200 hover:bg-brand-50/70 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 xl:w-[calc((100%_-_70px)/8)] lg:w-[calc((100%_-_60px)/7)] md-lg:w-[calc((100%_-_40px)/5)] md:w-[calc((100%_-_30px)/4)] sm:w-[calc((100%_-_15px)/2.5)] ${responsiveClass}`}
  >
    <div className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-shadow duration-200 group-hover:shadow-[0_4px_10px_rgba(124,58,237,0.14)] ${all ? 'border border-dashed border-brand-300 bg-brand-50 text-brand-700' : image ? 'bg-white' : `bg-gradient-to-br ${gradient}`}`}>
      {image ? (
        <img
          src={resolveImageUrl(image)}
          alt={`${label} ${t('category.imageAltSuffix')}`}
          loading='lazy'
          decoding='async'
          className='h-full w-full rounded-lg object-contain p-1 transition-transform duration-200 group-hover:scale-[1.03]'
        />
      ) : all ? (
        <FaLayerGroup size={15} className='transition-transform duration-200 group-hover:scale-105' />
      ) : (
        <span className='text-base text-white transition-transform duration-200 group-hover:scale-105'>{icon}</span>
      )}
      {badge && (
        <span className='absolute -right-1 -top-1 rounded-full bg-slate-950/70 px-1 py-0.5 text-[7px] font-extrabold uppercase tracking-wide text-white backdrop-blur-sm'>
          {badge}
        </span>
      )}
    </div>
    <span className='min-w-0 flex-1 truncate text-left text-xs font-semibold leading-tight text-slate-700 transition-colors group-hover:text-brand-700'>
      {label}
    </span>
  </Link>
  )
}

const CategoryStrip = ({ normalCategories = [], digitalCategories = [] }) => {
  const { t } = useTranslation()
  const hasNormal = normalCategories.length > 0
  const hasDigital = digitalCategories.length > 0
  const [tab, setTab] = useState('normal')
  const [progress, setProgress] = useState(0)
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)
  const railRef = useRef(null)

  const activeTab = tab === 'normal' && !hasNormal ? 'digital' : tab === 'digital' && !hasDigital ? 'normal' : tab
  const source = activeTab === 'normal' ? normalCategories : digitalCategories

  const cards = useMemo(() => source.map((category, index) => ({
    key: category._id || category.slug || category.name || index,
    to: activeTab === 'normal'
      ? (category.slug ? `/kateqoriya/${encodeURIComponent(category.slug)}` : `/products?category=${encodeURIComponent(category.name)}`)
      : `/digital/${category.slug}`,
    label: getCategoryLabel(category.name),
    image: activeTab === 'normal' ? category.image : null,
    icon: activeTab === 'digital' ? (category.icon || '🎮') : null,
    gradient: GRADIENTS[index % GRADIENTS.length],
    badge: activeTab === 'digital' ? t('category.badgeDigital') : null,
  })), [activeTab, source, t])

  const visibleCards = cards.length ? cards : DISCOVERY.map((item, index) => ({
    key: `discovery-${index}`,
    to: '/official',
    label: t(item.labelKey),
    icon: <item.icon />,
    gradient: item.gradient,
    badge: t('category.badgeOfficial'),
  }))

  const updateRail = () => {
    const rail = railRef.current
    if (!rail) return
    const max = Math.max(0, rail.scrollWidth - rail.clientWidth)
    setCanPrev(rail.scrollLeft > 4)
    setCanNext(rail.scrollLeft < max - 4)
    setProgress(max ? (rail.scrollLeft / max) * 100 : 100)
  }

  useEffect(() => {
    const rail = railRef.current
    if (!rail) return undefined
    rail.scrollTo({ left: 0, behavior: 'smooth' })
    const frame = requestAnimationFrame(updateRail)
    const observer = new ResizeObserver(updateRail)
    observer.observe(rail)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [activeTab, visibleCards.length])

  const scroll = direction => {
    const rail = railRef.current
    if (!rail) return
    rail.scrollBy({ left: direction * Math.max(rail.clientWidth * 0.72, 260), behavior: 'smooth' })
  }

  return (
    <section className='mx-auto mt-3 w-full max-w-marketplace px-4 md-lg:px-5 lg:px-8' aria-labelledby='category-title'>
      <div className='overflow-hidden rounded-2xl border border-slate-200/80 bg-white px-4 py-3 shadow-sm sm:px-3 sm:py-2.5'>
        <div className='mb-2 flex items-center justify-between gap-4 sm:items-start'>
          <div>
            <div className='flex items-center gap-2'>
              <span className='flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-600'><FaLayerGroup size={13} /></span>
              <h2 id='category-title' className='text-lg font-extrabold tracking-tight text-slate-900'>{t('category.title')}</h2>
            </div>
          </div>

          <div className='flex shrink-0 items-center gap-2'>
            {(hasNormal || hasDigital) && (
              <div className='mr-1 flex rounded-xl bg-slate-100 p-1'>
                <button type='button' onClick={() => setTab('normal')} disabled={!hasNormal} className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all disabled:cursor-not-allowed disabled:opacity-40 ${activeTab === 'normal' ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>{t('category.tabPhysical')}</button>
                <button type='button' onClick={() => setTab('digital')} disabled={!hasDigital} className={`rounded-lg px-3 py-1.5 text-xs font-bold transition-all disabled:cursor-not-allowed disabled:opacity-40 ${activeTab === 'digital' ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}>{t('category.tabDigital')}</button>
              </div>
            )}
            <div className='flex gap-1.5 sm:hidden'>
              <button type='button' aria-label={t('category.prevAria')} onClick={() => scroll(-1)} disabled={!canPrev} className='flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-brand-300 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-35'><FaArrowLeft size={12} /></button>
              <button type='button' aria-label={t('category.nextAria')} onClick={() => scroll(1)} disabled={!canNext} className='flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-brand-300 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-35'><FaArrowRight size={12} /></button>
            </div>
          </div>
        </div>

        {!cards.length && (
          <p className='mb-2 rounded-lg border border-dashed border-brand-200 bg-brand-50/60 px-3 py-1.5 text-xs font-medium text-brand-800'>
            {t('category.empty')}
          </p>
        )}

        <div
          ref={railRef}
          onScroll={updateRail}
          className={`category-scrollbar flex snap-x snap-mandatory gap-2.5 overflow-x-auto overscroll-x-contain scroll-smooth pb-1 ${visibleCards.length < 5 ? 'justify-center sm:justify-start' : ''}`}
        >
          <CategoryCard to='/kateqoriyalar' label='Bütün kateqoriyalar' all responsiveClass='hidden md-lg:flex' />
          {visibleCards.map(({ key, ...card }) => <CategoryCard key={key} {...card} />)}
          <CategoryCard to='/market' label={t('category.all')} all responsiveClass='md-lg:hidden' />
        </div>

        <div className='mt-1 flex items-center gap-3'>
          <div className='h-1 flex-1 overflow-hidden rounded-full bg-slate-100'>
            <div className='h-full rounded-full bg-gradient-to-r from-brand-500 to-indigo-500 transition-[width] duration-200' style={{ width: `${Math.max(12, progress)}%` }} />
          </div>
          <span className='text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400'>{t('category.scroll')}</span>
        </div>
      </div>
    </section>
  )
}

export default CategoryStrip
